import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MediaItem, WatchHistoryItem } from '../types';
import { MediaCard } from './MediaCard';

interface MediaRowProps {
  id?: string;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  items: MediaItem[];
  onSelect: (item: MediaItem) => void;
  onPlay: (item: MediaItem, episodeIndex?: number, initialTime?: number) => void;
  onToggleWatchlist?: (item: MediaItem) => void;
  isInWatchlist?: (itemId: string) => boolean;
  historyMap?: Record<string, WatchHistoryItem>;
  maxLimit?: number;
  onViewAll?: () => void;
  directPlayOnSelect?: boolean;
}

const MediaRowComponent: React.FC<MediaRowProps> = ({
  id,
  title,
  subtitle,
  icon,
  items,
  onSelect,
  onPlay,
  onToggleWatchlist,
  isInWatchlist,
  historyMap = {},
  maxLimit,
  onViewAll,
  directPlayOnSelect = false
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  
  const BATCH_SIZE = 24;
  // Render an optimal initial slice (up to 24 or maxLimit) so TV scrolls smoothly without constant re-render thrashing
  const INITIAL_BATCH = maxLimit ? Math.min(items.length, maxLimit) : Math.min(items.length, BATCH_SIZE);
  const [visibleLimit, setVisibleLimit] = React.useState(INITIAL_BATCH);

  // Sync visibleLimit when items list changes
  React.useEffect(() => {
    setVisibleLimit(maxLimit ? Math.min(items.length, maxLimit) : Math.min(items.length, 24));
  }, [items.length, maxLimit]);

  const handleScroll = () => {
    if (!rowRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = rowRef.current;
    if (scrollLeft + clientWidth >= scrollWidth - 400 && visibleLimit < items.length) {
      setVisibleLimit(prev => Math.min(items.length, prev + 16));
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (rowRef.current) {
      const scrollAmount = rowRef.current.clientWidth * 0.75;
      rowRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!items || items.length === 0) return null;

  const displayItems = items.slice(0, visibleLimit);
  const remainingCount = items.length - displayItems.length;

  const handleLoadMore = () => {
    setVisibleLimit(prev => Math.min(items.length, prev + BATCH_SIZE));
  };

  const handleResetLimit = () => {
    setVisibleLimit(maxLimit || BATCH_SIZE);
  };

  const rowId = id || `row-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div id={rowId} className="py-2.5 sm:py-3.5 relative group/row">
      {/* Row Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-2 flex items-end justify-between">
        <div className="flex items-center gap-2">
          {icon && <div className="text-amber-400">{icon}</div>}
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display font-bold text-base sm:text-lg md:text-xl text-white tracking-tight">
                {title}
              </h2>
              <span className="text-[11px] font-semibold px-2 py-0.2 rounded-full bg-white/5 text-slate-400 border border-white/5">
                {displayItems.length} / {items.length}
              </span>
            </div>
            {subtitle && (
              <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {onViewAll && (
            <button
              id={`btn-viewall-${rowId}`}
              data-focusable="true"
              tabIndex={0}
              onClick={onViewAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onViewAll();
                }
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/25 border border-amber-500/30 hover:border-amber-400 text-amber-300 hover:text-amber-200 text-xs font-bold transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer shadow-sm"
              title={`Mostra tots els títols de ${title}`}
            >
              <span>Mostra tots</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}

          {remainingCount > 0 && (
            <button
              tabIndex={-1}
              onClick={handleLoadMore}
              className="text-xs font-semibold text-amber-400 hover:text-amber-300 hover:underline px-2 py-0.5 cursor-pointer"
            >
              Carrega 20 més (+{remainingCount})
            </button>
          )}
          {visibleLimit > (maxLimit || 20) && (
            <button
              tabIndex={-1}
              onClick={handleResetLimit}
              className="text-xs font-semibold text-slate-400 hover:text-slate-200 hover:underline px-2 py-0.5 cursor-pointer"
            >
              Redueix a {maxLimit || 20}
            </button>
          )}

          {/* Scroll Buttons */}
          <div className="hidden sm:flex items-center gap-1 ml-1">
            <button
              tabIndex={-1}
              onClick={() => scroll('left')}
              className="p-1 rounded-full bg-white/5 hover:bg-white/15 text-slate-300 hover:text-white border border-white/5 transition-colors"
              title="Desplaça a l'esquerra"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              tabIndex={-1}
              onClick={() => scroll('right')}
              className="p-1 rounded-full bg-white/5 hover:bg-white/15 text-slate-300 hover:text-white border border-white/5 transition-colors"
              title="Desplaça a la dreta"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Cards Scrollable Container */}
      <div className="relative">
        <div
          ref={rowRef}
          onScroll={handleScroll}
          className="flex items-stretch gap-3 sm:gap-4 overflow-x-auto no-scrollbar px-4 sm:px-6 lg:px-8 py-1.5"
        >
          {displayItems.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              onSelect={onSelect}
              onPlay={onPlay}
              onToggleWatchlist={onToggleWatchlist}
              isInWatchlist={isInWatchlist ? isInWatchlist(item.id) : false}
              historyItem={historyMap[item.id]}
              directPlayOnSelect={directPlayOnSelect}
            />
          ))}

          {remainingCount > 0 && (
            <button
              id={`btn-loadmore-${rowId}`}
              data-focusable="true"
              onClick={handleLoadMore}
              className="flex-shrink-0 w-32 sm:w-36 aspect-[2/3] rounded-xl bg-white/5 hover:bg-white/10 border border-dashed border-amber-500/30 hover:border-amber-400 flex flex-col items-center justify-center gap-1.5 text-slate-300 hover:text-white transition-all p-3 text-center group cursor-pointer outline-none focus:border-amber-400 focus:bg-white/15"
            >
              <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center group-hover:scale-110 group-focus:scale-110 transition-transform">
                <ChevronRight className="w-4 h-4" />
              </div>
              <span className="text-xs font-bold text-amber-300">
                +20 títols més
              </span>
              <span className="text-[10px] text-slate-400 font-medium">
                ({remainingCount} restants)
              </span>
            </button>
          )}

          {onViewAll && (
            <button
              id={`card-viewall-${rowId}`}
              data-focusable="true"
              tabIndex={0}
              onClick={onViewAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onViewAll();
                }
              }}
              className="group relative flex-shrink-0 w-32 sm:w-36 md:w-40 lg:w-44 aspect-[2/3] tv-card rounded-xl bg-gradient-to-b from-[#121622] to-[#0a0d14] border border-amber-500/30 hover:border-amber-400 flex flex-col items-center justify-center gap-2 p-3 text-center transition-all duration-200 outline-none hover:-translate-y-1 focus:-translate-y-1 cursor-pointer focus:border-amber-400 shadow-md"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center group-hover:scale-110 group-focus:scale-110 transition-transform">
                <ChevronRight className="w-5 h-5" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-300 group-hover:text-amber-200">
                Mostra tots
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-400 font-medium">
                {items.length} títols
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const MediaRow = React.memo(MediaRowComponent);
